import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
} from "reactstrap";

import * as Yup from "yup";
import { useFormik } from "formik";

import { useDispatch, useSelector } from "react-redux";

import Breadcrumb from "../../components/Common/Breadcrumb";
import avatar from "../../assets/images/users/avatar-1.jpg";

import { editProfile, getProfile, resetProfileFlag } from "../../store/actions";

const UserProfile = () => {
  const dispatch = useDispatch();

  const { data, loading, updating, error, success } = useSelector(
    (state: any) => state.profile
  );

  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [resetPasswordOnSuccess, setResetPasswordOnSuccess] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    document.title = "Profile | Dashboard";
  }, []);

  useEffect(() => {
    dispatch(getProfile());
  }, [dispatch]);

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => dispatch(resetProfileFlag()), 3000);
    setServerErrors({});
    return () => clearTimeout(timer);
  }, [dispatch, success]);

  useEffect(() => {
    const apiErrors = error?.response?.data?.errors;
    if (apiErrors && typeof apiErrors === "object") {
      const nextErrors: Record<string, string> = {};
      Object.keys(apiErrors).forEach((key) => {
        const value = apiErrors[key];
        nextErrors[key] = Array.isArray(value) ? value[0] : String(value);
      });
      setServerErrors(nextErrors);
    }
  }, [error]);

  const errorMessage = useMemo(() => {
    if (!error) return "";
    if (typeof error === "string") return error;
    return (
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "Something went wrong. Please try again."
    );
  }, [error]);

  const getFieldError = (formikError: unknown, serverError?: string) => {
    if (typeof formikError === "string") return formikError;
    return serverError || "";
  };

  const roles = useMemo(() => {
    if (!Array.isArray(data?.roles)) return [];
    return data.roles.map((role: any) => role?.name).filter(Boolean);
  }, [data]);

  const offices = useMemo(() => {
    if (!Array.isArray(data?.offices)) return [];
    return data.offices.map((office: any) => office?.name).filter(Boolean);
  }, [data]);

  const infoForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: data?.name || "",
      email: data?.email || "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Please enter your name"),
      email: Yup.string().email("Invalid email").required("Please enter your email"),
    }),
    onSubmit: (values) => {
      setServerErrors({});
      dispatch(editProfile(values));
    },
  });

  const passwordForm = useFormik({
    initialValues: {
      current_password: "",
      password: "",
      password_confirmation: "",
    },
    validationSchema: Yup.object({
      current_password: Yup.string().required("Enter your current password"),
      password: Yup.string().min(8, "Minimum 8 characters").required("Enter a new password"),
      password_confirmation: Yup.string()
        .oneOf([Yup.ref("password")], "Passwords do not match")
        .required("Confirm your new password"),
    }),
    onSubmit: (values) => {
      setServerErrors({});
      setResetPasswordOnSuccess(true);
      dispatch(editProfile(values));
    },
  });

  useEffect(() => {
    if (success && resetPasswordOnSuccess) {
      passwordForm.resetForm();
      setResetPasswordOnSuccess(false);
    }
  }, [success, resetPasswordOnSuccess, passwordForm]);

  const isInfoDisabled = loading || updating || !infoForm.dirty || !infoForm.isValid;
  const isPasswordDisabled =
    loading || updating || !passwordForm.dirty || !passwordForm.isValid;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Dashboard" breadcrumbItem="Profile" />

          {errorMessage ? <Alert color="danger">{errorMessage}</Alert> : null}
          {success ? <Alert color="success">{success}</Alert> : null}

          <Row>
            <Col xl={4} lg={5}>
              <Card>
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="ms-3">
                      <img
                        src={avatar}
                        alt="Profile avatar"
                        className="avatar-md rounded-circle img-thumbnail"
                      />
                    </div>
                    <div className="flex-grow-1 align-self-center ms-3">
                      <h5 className="mb-1">{data?.name || "—"}</h5>
                      <p className="text-muted mb-2">{data?.email || "—"}</p>
                      <div className="text-muted">
                        <div>User ID: {data?.id ?? "—"}</div>
                        <div>Team ID: {data?.team?.id ?? data?.team_id ?? "—"}</div>
                        <div>Team: {data?.team?.name ?? "—"}</div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="text-muted mb-2">Roles</div>
                    <div className="d-flex flex-wrap gap-2">
                      {roles.length
                        ? roles.map((role: string) => (
                            <Badge key={role} color="primary" pill>
                              {role}
                            </Badge>
                          ))
                        : <span className="text-muted">—</span>}
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="text-muted mb-2">Offices</div>
                    <div className="d-flex flex-wrap gap-2">
                      {offices.length
                        ? offices.map((office: string) => (
                            <Badge key={office} color="secondary" pill>
                              {office}
                            </Badge>
                          ))
                        : <span className="text-muted">—</span>}
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xl={8} lg={7}>
              <Card>
                <CardBody>
                  <h5 className="card-title mb-3">Personal Information</h5>
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      infoForm.handleSubmit();
                    }}
                  >
                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label className="form-label">Full Name</Label>
                          <Input
                            name="name"
                            type="text"
                            autoComplete="name"
                            value={infoForm.values.name}
                            onChange={infoForm.handleChange}
                            onBlur={infoForm.handleBlur}
                            invalid={!!(infoForm.touched.name && infoForm.errors.name) || !!serverErrors.name}
                          />
                          <FormFeedback type="invalid">
                            {getFieldError(infoForm.errors.name, serverErrors.name)}
                          </FormFeedback>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label className="form-label">Email</Label>
                          <Input
                            name="email"
                            type="email"
                            autoComplete="email"
                            value={infoForm.values.email}
                            onChange={infoForm.handleChange}
                            onBlur={infoForm.handleBlur}
                            invalid={!!(infoForm.touched.email && infoForm.errors.email) || !!serverErrors.email}
                          />
                          <FormFeedback type="invalid">
                            {getFieldError(infoForm.errors.email, serverErrors.email)}
                          </FormFeedback>
                        </div>
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-end">
                      <Button type="submit" color="primary" disabled={isInfoDisabled}>
                        {updating ? "Saving..." : "Save Changes"}
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <h5 className="card-title mb-3">Change Password</h5>
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      passwordForm.handleSubmit();
                    }}
                  >
                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label className="form-label">Current Password</Label>
                          <div className="input-group">
                            <Input
                              name="current_password"
                              type={showCurrentPassword ? "text" : "password"}
                              autoComplete="current-password"
                              value={passwordForm.values.current_password}
                              onChange={passwordForm.handleChange}
                              onBlur={passwordForm.handleBlur}
                              invalid={
                                !!(passwordForm.touched.current_password &&
                                  passwordForm.errors.current_password) || !!serverErrors.current_password
                              }
                            />
                            <button
                              className="btn btn-light"
                              type="button"
                              onClick={() => setShowCurrentPassword((prev) => !prev)}
                              aria-label={showCurrentPassword ? "Hide password" : "Show password"}
                            >
                              <i className={showCurrentPassword ? "mdi mdi-eye-off-outline" : "mdi mdi-eye-outline"}></i>
                            </button>
                            <FormFeedback type="invalid">
                              {getFieldError(passwordForm.errors.current_password, serverErrors.current_password)}
                            </FormFeedback>
                          </div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label className="form-label">New Password</Label>
                          <div className="input-group">
                            <Input
                              name="password"
                              type={showNewPassword ? "text" : "password"}
                              autoComplete="new-password"
                              value={passwordForm.values.password}
                              onChange={passwordForm.handleChange}
                              onBlur={passwordForm.handleBlur}
                              invalid={
                                !!(passwordForm.touched.password && passwordForm.errors.password) ||
                                !!serverErrors.password
                              }
                            />
                            <button
                              className="btn btn-light"
                              type="button"
                              onClick={() => setShowNewPassword((prev) => !prev)}
                              aria-label={showNewPassword ? "Hide password" : "Show password"}
                            >
                              <i className={showNewPassword ? "mdi mdi-eye-off-outline" : "mdi mdi-eye-outline"}></i>
                            </button>
                            <FormFeedback type="invalid">
                              {getFieldError(passwordForm.errors.password, serverErrors.password)}
                            </FormFeedback>
                          </div>
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label className="form-label">Confirm New Password</Label>
                          <div className="input-group">
                            <Input
                              name="password_confirmation"
                              type={showConfirmPassword ? "text" : "password"}
                              autoComplete="new-password"
                              value={passwordForm.values.password_confirmation}
                              onChange={passwordForm.handleChange}
                              onBlur={passwordForm.handleBlur}
                              invalid={
                                !!(passwordForm.touched.password_confirmation &&
                                  passwordForm.errors.password_confirmation) ||
                                !!serverErrors.password_confirmation
                              }
                            />
                            <button
                              className="btn btn-light"
                              type="button"
                              onClick={() => setShowConfirmPassword((prev) => !prev)}
                              aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                            >
                              <i className={showConfirmPassword ? "mdi mdi-eye-off-outline" : "mdi mdi-eye-outline"}></i>
                            </button>
                            <FormFeedback type="invalid">
                              {getFieldError(passwordForm.errors.password_confirmation, serverErrors.password_confirmation)}
                            </FormFeedback>
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-end">
                      <Button type="submit" color="danger" disabled={isPasswordDisabled}>
                        {updating ? "Updating..." : "Update Password"}
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default UserProfile;
